import { useState } from 'react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Search,
  Database,
  Loader2,
  Zap,
  Factory,
  Truck,
  Droplets,
  AlertCircle,
  MapPin,
  Plus,
  ChevronDown,
  ChevronRight,
} from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { lcaAPI } from '@/lib/api/client'
import { projectAPI } from '@/lib/api/projects'
import { getApiErrorMessage } from '@/lib/api/errors'
import { toast } from 'sonner'
import { useWorkspaceStore } from '@/store/workspaceStore'
import type { ActivityWithExchanges, ProjectExchange } from '@/lib/types/project-graph'

interface SearchResult {
  name: string
  code: string
  location: string
  unit: string
  database: string
  reference_product?: string
  relevance_score?: number
  source: 'ecoinvent' | 'custom' | 'biosphere'
  // Biosphere-only metadata (present when source === 'biosphere')
  compartment?: string
  sub_compartment?: string
}

// Raw biosphere-flow row as returned by the search API (before mapping to SearchResult).
interface RawBiosphereFlow {
  name: string
  code: string
  unit: string
  compartment?: string
  sub_compartment?: string
}

type SearchMode = 'activities' | 'biosphere'

interface ComponentLibraryProps {
  projectDatabase?: string
  activities?: ActivityWithExchanges[]
  onExchangeCreated?: () => void
}

export function ComponentLibrary({
  projectDatabase,
  activities = [],
  onExchangeCreated,
}: ComponentLibraryProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [results, setResults] = useState<SearchResult[]>([])
  const [hasSearched, setHasSearched] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expandedCode, setExpandedCode] = useState<string | null>(null)
  const [mode, setMode] = useState<SearchMode>('activities')

  const handleSearch = async (query?: string, modeOverride?: SearchMode) => {
    const q = query ?? searchQuery
    const m = modeOverride ?? mode
    if (!q.trim() || !projectDatabase) return
    setIsSearching(true)
    setHasSearched(true)
    setError(null)
    setExpandedCode(null)
    try {
      if (m === 'biosphere') {
        const response = await lcaAPI.searchBiosphere({
          q: q.trim(),
          database: projectDatabase!,
          limit: 30,
        })
        const data = response.data
        const flows: RawBiosphereFlow[] = data.biosphere_flows || data.flows || data.results || []
        const merged: SearchResult[] = flows.map((f) => ({
          name: f.name,
          code: f.code,
          location: f.compartment || '-',
          unit: f.unit,
          database: projectDatabase!,
          compartment: f.compartment,
          sub_compartment: f.sub_compartment,
          source: 'biosphere' as const,
        }))
        setResults(merged)
      } else {
        const response = await lcaAPI.searchActivities({
          q: q.trim(),
          database: projectDatabase!,
          limit: 30,
        })
        const data = response.data
        const merged: SearchResult[] = [
          ...(data.ecoinvent_activities || []).map((a: Omit<SearchResult, 'source' | 'database'>) => ({
            ...a,
            database: projectDatabase!,
            source: 'ecoinvent' as const,
          })),
          ...(data.custom_activities || []).map((a: Omit<SearchResult, 'source' | 'database'>) => ({
            ...a,
            database: 'custom',
            source: 'custom' as const,
          })),
        ]
        setResults(merged)
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Search failed'))
      setResults([])
    } finally {
      setIsSearching(false)
    }
  }

  const switchMode = (next: SearchMode) => {
    if (next === mode) return
    setMode(next)
    setResults([])
    setHasSearched(false)
    setError(null)
    setExpandedCode(null)
    if (searchQuery.trim() && projectDatabase) {
      handleSearch(searchQuery, next)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const handleQuickCategory = (category: string) => {
    setSearchQuery(category)
    if (projectDatabase) handleSearch(category)
  }

  const canSearch = searchQuery.trim().length > 0 && !!projectDatabase
  // Show the system-model qualifier ("cutoff" / "apos" / "consequential") explicitly.
  // Stripping it silently hides a load-bearing identifier — ecoinvent ships
  // multiple system models per version and they are not interchangeable.
  // Collapse the "ecoinvent-" prefix to "ei " for space, but keep the suffix.
  const formatDbName = (db: string) => {
    const m = db.match(/^ecoinvent-([\d.]+)-(.+)$/)
    if (m) return `ei ${m[1]} (${m[2]})`
    return db
  }

  return (
    <div className="h-full flex flex-col">
      {/* Search */}
      <div className="p-2 border-b border-border space-y-2">
        {projectDatabase ? (
          <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground px-0.5">
            <Database className="h-3 w-3 shrink-0" />
            <span>{formatDbName(projectDatabase)}</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground/60 px-0.5">
            <Database className="h-3 w-3 shrink-0" />
            <span>No project selected</span>
          </div>
        )}
        {/* Mode toggle: Activities vs Biosphere flows */}
        <div className="flex rounded-md border border-border overflow-hidden text-[11px]">
          <button
            type="button"
            onClick={() => switchMode('activities')}
            className={`flex-1 py-1 transition-colors ${mode === 'activities' ? 'bg-accent text-foreground' : 'text-muted-foreground hover:bg-accent/40'}`}
            aria-pressed={mode === 'activities'}
          >
            Activities
          </button>
          <button
            type="button"
            onClick={() => switchMode('biosphere')}
            className={`flex-1 py-1 transition-colors border-l border-border ${mode === 'biosphere' ? 'bg-accent text-foreground' : 'text-muted-foreground hover:bg-accent/40'}`}
            aria-pressed={mode === 'biosphere'}
          >
            Biosphere
          </button>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder={
              mode === 'biosphere' ? 'Search biosphere flows...' : 'Search activities...'
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-8 pl-8 pr-16 text-xs"
          />
          <Button
            size="sm"
            variant="ghost"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-6 px-2 text-xs"
            onClick={() => handleSearch()}
            disabled={isSearching || !canSearch}
          >
            {isSearching ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Search'}
          </Button>
        </div>
      </div>

      {/* Results */}
      <ScrollArea className="flex-1">
        <div className="p-1.5">
          {error ? (
            <div className="text-center py-6">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 text-destructive/60" />
              <p className="text-xs text-destructive">{error}</p>
            </div>
          ) : !hasSearched ? (
            <div className="text-center py-8">
              <Search className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
              <p className="text-xs text-muted-foreground">
                {!projectDatabase
                  ? 'Select a project first to search its database'
                  : mode === 'biosphere'
                    ? 'Search for biosphere flows (emissions, resources)'
                    : 'Search for activities to add as exchanges'}
              </p>
            </div>
          ) : results.length === 0 && !isSearching ? (
            <div className="text-center py-8 text-muted-foreground text-xs">
              No results for "{searchQuery}"
            </div>
          ) : (
            <div>
              <p className="text-[10px] text-muted-foreground px-1.5 mb-1">
                {results.length} result{results.length !== 1 ? 's' : ''}
              </p>
              {results.map((r) => (
                <ComponentItem
                  key={`${r.source}-${r.code}`}
                  result={r}
                  expanded={expandedCode === r.code}
                  onToggle={() => setExpandedCode(expandedCode === r.code ? null : r.code)}
                  activities={activities}
                  onExchangeCreated={onExchangeCreated}
                />
              ))}
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Quick Categories */}
      <div className="p-2 border-t border-border">
        <p className="text-[10px] text-muted-foreground mb-1.5">Suggested searches</p>
        <div className="grid grid-cols-2 gap-1">
          <QuickBtn
            icon={Zap}
            label="Energy"
            onClick={() => handleQuickCategory('electricity')}
            disabled={!projectDatabase}
          />
          <QuickBtn
            icon={Factory}
            label="Materials"
            onClick={() => handleQuickCategory('steel')}
            disabled={!projectDatabase}
          />
          <QuickBtn
            icon={Truck}
            label="Transport"
            onClick={() => handleQuickCategory('transport freight')}
            disabled={!projectDatabase}
          />
          <QuickBtn
            icon={Droplets}
            label="Water"
            onClick={() => handleQuickCategory('water')}
            disabled={!projectDatabase}
          />
        </div>
      </div>
    </div>
  )
}

// --- Sub-components ---

interface ComponentItemProps {
  result: SearchResult
  expanded: boolean
  onToggle: () => void
  activities: ActivityWithExchanges[]
  onExchangeCreated?: () => void
}

function ComponentItem({
  result,
  expanded,
  onToggle,
  activities,
  onExchangeCreated,
}: ComponentItemProps) {
  const [adding, setAdding] = useState(false)
  const [amount, setAmount] = useState('1')
  const [selectedActivityId, setSelectedActivityId] = useState('')
  const { upsertBgSynthExchange, selectNode, setRightTab } = useWorkspaceStore()

  // Explicit affordance to surface this library result in the right-panel
  // Background tab. Used to be the row click itself, but that hijacked the
  // user's current activity selection on every expand. Now it's opt-in via a
  // dedicated "Inspect" button inside the expanded section.
  const inspectInBackground = () => {
    if (result.source !== 'ecoinvent') return
    const synthId = `bg::${result.code}`
    const synth: ProjectExchange = {
      id: synthId,
      activity_id: '__bg_synth__',
      exchange_type: 'technosphere',
      name: result.name,
      amount: 1,
      unit: result.unit,
      input_database: result.database,
      input_activity_code: result.code,
      created_at: '',
    }
    upsertBgSynthExchange(synth)
    selectNode(synthId, 'exchange')
    setRightTab('background')
  }

  const handleAdd = async () => {
    if (!selectedActivityId || !amount) return
    const numAmt = parseFloat(amount)
    if (isNaN(numAmt) || numAmt <= 0) {
      toast.error('Amount must be a positive number')
      return
    }
    // Fail-fast: source must be present on every search row. If a future result
    // shape ever omits it, surface the bug instead of silently routing to ecoinvent.
    if (result.source !== 'ecoinvent' && result.source !== 'custom') {
      toast.error(`Cannot add exchange: unknown source "${String(result.source)}" on result`)
      return
    }
    setAdding(true)
    try {
      if (result.source === 'custom') {
        // Custom-source rows reference a project activity (not an ecoinvent node)
        // and must go through the /exchanges/custom endpoint.
        await projectAPI.createCustomExchange(selectedActivityId, {
          name: result.name,
          amount: numAmt,
          unit: result.unit,
          exchange_type: 'technosphere',
          input_activity_code: result.code,
        })
      } else {
        await projectAPI.createEcoinventExchange(selectedActivityId, {
          amount: numAmt,
          exchange_type: 'technosphere',
          input_database: result.database,
          input_activity_code: result.code,
        })
      }
      toast.success(`Added: ${result.name}`)
      onExchangeCreated?.()
      setSelectedActivityId('')
      setAmount('1')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to add exchange'))
    } finally {
      setAdding(false)
    }
  }

  return (
    <div
      className="border-b border-border/50 last:border-0"
      draggable
      onDragStart={(e) => {
        // Carry an explicit `exchange_type` hint so the drop handler doesn't
        // have to guess. Biosphere rows drag as 'biosphere'; ecoinvent and
        // custom-database items default to 'technosphere' (drop dialog still
        // lets the user override to production/substitution).
        const exchangeType = result.source === 'biosphere' ? 'biosphere' : 'technosphere'
        const payload = { ...result, exchange_type: exchangeType }
        e.dataTransfer.setData('application/json', JSON.stringify(payload))
        e.dataTransfer.effectAllowed = 'copy'
      }}
    >
      {/* Compact row — click to expand. Original behavior: only toggles the
          inline detail; never touches activity selection or the right tab. */}
      <button
        onClick={onToggle}
        className="w-full text-left px-2 py-1.5 hover:bg-accent/40 transition-colors flex items-start gap-1.5"
      >
        <span className="mt-0.5 shrink-0">
          {expanded ? (
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3 w-3 text-muted-foreground" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs leading-tight line-clamp-2">{result.name}</p>
          <div className="flex items-center gap-1 mt-0.5 text-[10px] text-muted-foreground">
            <MapPin className="h-2.5 w-2.5 shrink-0" />
            <span>{result.location}</span>
            <span className="text-muted-foreground/40">·</span>
            <span>{result.unit}</span>
          </div>
        </div>
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="px-2 pb-2 ml-5 space-y-2">
          {result.reference_product && (
            <div className="text-[10px]">
              <span className="text-muted-foreground">Product: </span>
              <span>{result.reference_product}</span>
            </div>
          )}
          <div className="text-[10px] font-mono text-muted-foreground break-all">{result.code}</div>
          {result.source === 'ecoinvent' && (
            <button
              onClick={inspectInBackground}
              className="text-[10px] text-primary hover:underline"
              title="Open in the right-panel Background tab"
            >
              View dataset details →
            </button>
          )}
          {result.source === 'custom' && (
            <Badge variant="secondary" className="text-[9px] h-4">
              Custom
            </Badge>
          )}
          {result.source === 'biosphere' && (
            <div className="flex flex-wrap gap-1 items-center">
              <Badge variant="secondary" className="text-[9px] h-4">
                Biosphere
              </Badge>
              {result.sub_compartment && (
                <span className="text-[10px] text-muted-foreground">
                  {result.compartment} / {result.sub_compartment}
                </span>
              )}
            </div>
          )}

          {/* Add to activity — biosphere flows are read-only here; adding biosphere
              exchanges happens via the Activity panel (deferred to Group A's drag
              payload work). */}
          {result.source === 'biosphere' ? (
            <p className="text-[10px] text-muted-foreground italic pt-1 border-t border-border/50">
              Add biosphere flows from the Activity panel.
            </p>
          ) : (
            activities.length > 0 && (
              <div className="space-y-1.5 pt-1 border-t border-border/50">
                <p className="text-[10px] font-medium text-muted-foreground">Add to activity</p>
                <select
                  value={selectedActivityId}
                  onChange={(e) => setSelectedActivityId(e.target.value)}
                  className="w-full h-7 text-[11px] rounded-md border border-border bg-background px-1.5"
                >
                  <option value="">Select activity...</option>
                  {activities.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} ({a.location})
                    </option>
                  ))}
                </select>
                {selectedActivityId && (
                  <div className="flex items-center gap-1.5">
                    <Input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="h-7 text-[11px] flex-1"
                      min="0"
                      step="any"
                      placeholder="Amount"
                    />
                    <span className="text-[10px] text-muted-foreground shrink-0">
                      {result.unit}
                    </span>
                    <Button
                      size="sm"
                      className="h-7 px-2 text-[11px] gap-1 shrink-0"
                      onClick={handleAdd}
                      disabled={adding}
                    >
                      {adding ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Plus className="h-3 w-3" />
                      )}
                      Add
                    </Button>
                  </div>
                )}
              </div>
            )
          )}
        </div>
      )}
    </div>
  )
}

function QuickBtn({
  icon: Icon,
  label,
  onClick,
  disabled,
}: {
  icon: typeof Zap
  label: string
  onClick: () => void
  disabled?: boolean
}) {
  const button = (
    <Button
      variant="outline"
      size="sm"
      className="h-7 text-xs gap-1.5 justify-start w-full"
      onClick={onClick}
      disabled={disabled}
      aria-label={disabled ? `${label} (select a project first)` : label}
    >
      <Icon className="h-3 w-3" />
      {label}
    </Button>
  )
  if (!disabled) return button
  // Disabled buttons swallow pointer events, so wrap the trigger in a span
  // that still receives hover and gets the tooltip.
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-block w-full cursor-not-allowed">{button}</span>
      </TooltipTrigger>
      <TooltipContent side="top">Select a project first</TooltipContent>
    </Tooltip>
  )
}
